import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { Check, Plane } from "lucide-react";

export default function WhyUs() {
  const { t } = useI18n();

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
      <div className="absolute inset-0 dot-grid opacity-20" />

      <div className="relative container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-10 h-px bg-lux" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
            {t.why.label}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.0 }}
          className="font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-tighter leading-[0.95] mb-12 max-w-5xl"
        >
          <div>{t.why.title_1}</div>
          <div className="italic font-extralight text-gradient-lux">
            {t.why.title_2}
          </div>
        </motion.h2>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 lg:gap-24 mt-20">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="text-base md:text-xl font-body text-white/70 leading-relaxed max-w-xl"
          >
            {t.why.paragraph}
          </motion.p>

          <div className="space-y-7">
            {t.why.pillars.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`why-pillar-${i}`}
                className="flex gap-4 pb-6 border-b border-white/10 last:border-b-0"
              >
                <div className="shrink-0 w-9 h-9 rounded-full luxury-glass flex items-center justify-center mt-0.5">
                  <Check size={14} strokeWidth={1.6} className="text-lux" />
                </div>
                <div>
                  <h4 className="font-display text-lg font-normal mb-1">{p.t}</h4>
                  <p className="text-sm text-white/55">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Commitment badge */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          data-testid="why-commitment"
          className="relative mt-24 luxury-glass rounded-2xl p-10 md:p-14 overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/[0.03] blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12">
            <div className="shrink-0 w-16 h-16 rounded-full bg-lux text-black flex items-center justify-center">
              <Plane size={22} strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.32em] text-lux mb-3">
                {t.why.commitment_label}
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight mb-3">
                {t.why.commitment_title}
              </h3>
              <p className="text-white/60 max-w-2xl">{t.why.commitment_desc}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
